import type { MenuContext, MenuScreen } from '../types.js';
import type { Difficulty } from '../../game/types.js';
import {
  createScreenShell,
  createMenuHeader,
  createCrestButton,
  createCycleBadge,
  el,
  withFocusPreserved,
} from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';

const DIFFICULTIES: readonly Difficulty[] = ['easy', 'medium', 'hard'];

function difficultyLabel(d: Difficulty): string {
  return t(`play.difficulty.${d}`);
}

export function createPlayMenuScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  function launch(ctx: MenuContext, difficulty: Difficulty): void {
    const go = (): void => ctx.startGame(difficulty);
    ctx.discardActiveSession(go);
  }

  function paint(root: HTMLElement, ctx: MenuContext): void {
    while (root.firstChild) root.removeChild(root.firstChild);
    const shell = createScreenShell();
    frame = shell.frame;
    const body = shell.body;
    body.style.justifyContent = 'space-between';

    body.appendChild(createMenuHeader({ title: t('play.title') }));

    const settings = ctx.getSettings();
    const currentDifficulty = settings.difficulty;

    const difficultyBadge = createCycleBadge<Difficulty>({
      label: t('settings.difficulty'),
      value: currentDifficulty,
      options: DIFFICULTIES,
      format: difficultyLabel,
      onChange: (next) => {
        ctx.updateSettings({ difficulty: next });
        withFocusPreserved(() => paint(root, ctx), root);
      },
    });

    const stack: HTMLElement[] = [];

    const session = ctx.getActiveSession();
    if (session) {
      stack.push(
        createCrestButton({
          label: t('play.resume'),
          primary: true,
          onClick: () => ctx.resumeActiveGame(),
        }),
      );
    }

    stack.push(
      createCrestButton({
        label: t('play.startButton'),
        primary: !session,
        onClick: () => launch(ctx, currentDifficulty),
      }),
    );

    const buttons = el('div', { class: 'button-stack' }, stack);

    const back = el('div', { class: 'button-row back-button-row' }, [
      createCrestButton({
        label: t('common.back'),
        small: true,
        onClick: () => ctx.navigate('menu'),
      }),
    ]);

    const center = el('div', { class: 'about-center' }, [difficultyBadge, buttons]);

    body.appendChild(center);
    body.appendChild(back);
    root.appendChild(frame);
  }

  return {
    mount(root, ctx) {
      paint(root, ctx);
    },
    update(ctx) {
      if (frame?.parentElement) paint(frame.parentElement, ctx);
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
